import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import { RoomsService } from '../../services/rooms.service';
import { roomsActions } from '.';
import { ApiError } from '../../models/common/api-error';
import { of } from 'rxjs';
import { RoomsState } from './rooms.state';
import { storeRoomsKey } from '../reducers';

@Injectable({
  providedIn: 'root',
})
export class RoomsEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private roomsService: RoomsService,
  ) {}

  needRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.needRoom),
      withLatestFrom(this.store$),
      filter(([action, store]) =>
        this.roomNeedsFetch(store[storeRoomsKey], action.id, action.includes),
      ),
      map(([action]) =>
        roomsActions.findOne({ id: action.id, includes: action.includes }),
      ),
    ),
  );

  findOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.findOne),
      mergeMap((action) => {
        const wantsFull = !action.includes;
        return this.roomsService.findOne(action.id, action.includes).pipe(
          map((response) =>
            roomsActions.findOneSuccess({
              room: response.data,
              full: wantsFull,
              includes: action.includes,
            }),
          ),
          catchError((error: ApiError) =>
            of(roomsActions.findError({ error: error.message })),
          ),
        );
      }),
    ),
  );

  private roomNeedsFetch(
    state: RoomsState,
    id: number,
    includes?: string[],
  ): boolean {
    const cachedRoom = state.entities[id];
    if (!cachedRoom) {
      return true;
    }
    if (!includes) {
      return !state.loadedFullById[id];
    }
    if (state.loadedFullById[id]) {
      return false;
    }
    const loadedIncludes = state.loadedIncludesById[id] ?? [];
    return !includes.every((include) => loadedIncludes.includes(include));
  }

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(roomsActions.findAll),
      withLatestFrom(this.store$),
      mergeMap(([action, store]) => {
        return this.roomsService.findAll(action.page).pipe(
          map((response) =>
            roomsActions.findAllSuccess({
              rooms: response.data,
              total: response.total,
            }),
          ),
          catchError((error: ApiError) =>
            of(roomsActions.findError({ error: error.message })),
          ),
        );
      }),
    ),
  );
}
